import type { EnhancedSectionContent } from '@/components/GenericComponent/GenericComponent';
import type { EnhancedSectionType } from '@/components/Section/Section';
import type { SwitchableContentSelectorContent } from '@/components/SwitchableContentSelector/SwitchableContentSelector';
import type {
  ContactCardCtaCf,
  HighlightContactCardsCf,
  SectionConstraintsCF,
} from '@/types';

const isHighlightContactCards = (
  content: EnhancedSectionContent
): content is HighlightContactCardsCf => {
  return content?.sys?.contentType?.sys?.id === 'highlightContactCards';
};

const isSectionConstraints = (
  // biome-ignore lint/suspicious/noExplicitAny: SectionType doesn't include SectionStateConstraintsList
  content: any
): content is SectionConstraintsCF => {
  return content?.sys?.contentType?.sys?.id === 'sectionConstraints';
};

const isSwitchableContentSelector = (
  content: EnhancedSectionContent
): content is SwitchableContentSelectorContent => {
  return content.sys.contentType.sys.id === 'switchableContentSelector';
};

const isContactCardCta = (channel: unknown): channel is ContactCardCtaCf => {
  if (!channel || typeof channel !== 'object') return false;

  const contentTypeId = (
    channel as {
      sys?: { contentType?: { sys?: { id?: string } } };
    }
  ).sys?.contentType?.sys?.id;

  return contentTypeId === 'contactCardCta';
};

const getSectionContent = (
  sections: EnhancedSectionType[]
): EnhancedSectionContent[] => {
  const content: EnhancedSectionContent[] = [];

  sections.forEach((section) => {
    const entries = section.fields?.content || [];

    entries.forEach((entry) => {
      if (isSectionConstraints(entry) && entry.fields.default) {
        content.push(...(entry.fields.default as EnhancedSectionContent[]));
      } else if (isSwitchableContentSelector(entry)) {
        const nestedSections = getSectionContent(
          entry.fields.sectionToggleSections
        );
        content.push(...nestedSections);
      } else {
        content.push(entry);
      }
    });
  });

  return content;
};

export function extractPhoneFromSections(
  sections: EnhancedSectionType[]
): string | undefined {
  const sectionContent = getSectionContent(sections);
  const contactCardsSections = sectionContent.filter(isHighlightContactCards);

  for (const section of contactCardsSections) {
    for (const card of section.fields.contactCards ?? []) {
      if (!card) continue;

      for (const channel of card.fields.contactChannels ?? []) {
        if (!isContactCardCta(channel)) continue;

        const { label, value } = channel.fields;
        if (value?.startsWith('tel:')) {
          return label || value.replace('tel:', '');
        }
      }
    }
  }

  return undefined;
}
